import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { buscarBadges, BADGE_DEFS } from '../utils/badges';
import { useToast } from '../context/ToastContext';
import { usePlan } from '../context/PlanContext';
import { Trophy, Sparkles, ChevronRight, Star, Building2, BookOpen, BookMarked, Moon, Sun, X, AlertTriangle, Users } from 'lucide-react';

function calcMedia(lista, campo) {
  const vals = lista.map(e => e[campo]).filter(v => v > 0);
  if (!vals.length) return null;
  return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
}

function Estrelas({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(i => (
        <span key={i} className={`text-xs ${i <= n ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
      ))}
    </div>
  );
}

function MediaDestaque({ lista, campo }) {
  const media = calcMedia(lista, campo);
  if (!media) return (
    <div className="text-center py-12">
      <div className="w-12 h-12 rounded-[14px] flex items-center justify-center mx-auto mb-3" style={{background:'#EDE9FF'}}><Trophy size={24} strokeWidth={1.6} color="#5B3CE4" /></div>
      <p className="text-sm text-ink-500 font-medium">Nenhuma avaliação ainda</p>
      <p className="text-xs text-ink-400 mt-1">As avaliações aparecerão aqui</p>
    </div>
  );
  return (
    <div className="bg-white border border-line-soft rounded-xl p-5 mb-4 flex items-center gap-5">
      <div className="text-center">
        <p className="text-4xl font-light text-ink-900">{media}</p>
        <div className="flex gap-0.5 justify-center mt-1">
          {[1,2,3,4,5].map(i => (
            <span key={i} className={`text-base ${i <= Math.round(parseFloat(media)) ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
          ))}
        </div>
      </div>
      <p className="text-sm text-ink-400">
        Baseado em {lista.length} avaliação{lista.length !== 1 ? 'ões' : ''}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function ConexaoCard({ p, seguindoSet, bloqueadosSet, menuConexaoId, setMenuConexaoId, onSeguir, onPararSeguir, onBloquear, onDesbloquear, navigate, seguindoCarregandoId }) {
  const isSeguindo = seguindoSet.has(p.id);
  const isBloqueado = bloqueadosSet.has(p.id);
  const menuAberto = menuConexaoId === p.id;
  const carregando = seguindoCarregandoId === p.id;

  return (
    <div className="bg-white border border-line-soft rounded-xl px-4 py-3 flex items-center gap-3">
      <div
        className="w-9 h-9 rounded-full bg-paper-2 flex items-center justify-center text-sm font-medium text-ink-500 flex-shrink-0 overflow-hidden cursor-pointer"
        onClick={() => p.slug && navigate(`/biblioteca/${p.slug}`)}>
        {p.foto_url
          ? <img src={p.foto_url} alt="" className="w-full h-full object-cover" />
          : (p.nome || 'U').charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0 cursor-pointer" onClick={() => p.slug && navigate(`/biblioteca/${p.slug}`)}>
        <p className="text-sm font-medium text-ink-900 truncate">{p.nome || 'Usuário'}</p>
        {p.username && <p className="text-xs text-ink-400">@{p.username}</p>}
      </div>
      <div className="relative flex-shrink-0" onClick={e => e.stopPropagation()}>
        {isBloqueado ? (
          <button
            onClick={() => setMenuConexaoId(menuAberto ? null : p.id)}
            className="text-xs px-3 py-1.5 border border-red-200 text-red-400 rounded-xl">
            Bloqueado
          </button>
        ) : isSeguindo ? (
          <button
            onClick={() => setMenuConexaoId(menuAberto ? null : p.id)}
            className="text-xs px-3 py-1.5 border border-line text-ink-500 rounded-xl flex items-center gap-1">
            Seguindo <span className="text-ink-300">▾</span>
          </button>
        ) : (
          <button
            onClick={() => !carregando && onSeguir(p)}
            disabled={carregando}
            className="text-xs px-3 py-1.5 bg-brand text-white rounded-xl transition-opacity disabled:opacity-60">
            {carregando ? '...' : 'Seguir'}
          </button>
        )}
        {menuAberto && (
          <div className="absolute right-0 top-9 bg-white border border-line-soft rounded-xl shadow-lg z-20 min-w-[148px] overflow-hidden">
            {isBloqueado ? (
              <button onClick={() => onDesbloquear(p.id)}
                className="w-full text-left px-4 py-2.5 text-xs text-ink-700 hover:bg-paper">
                Desbloquear
              </button>
            ) : (
              <>
                <button onClick={() => onPararSeguir(p.id)}
                  className="w-full text-left px-4 py-2.5 text-xs text-ink-700 hover:bg-paper border-b border-gray-50">
                  Parar de seguir
                </button>
                <button onClick={() => onBloquear(p.id)}
                  className="w-full text-left px-4 py-2.5 text-xs text-red-500 hover:bg-red-50">
                  Bloquear
                </button>
              </>
            )}
            <button onClick={() => setMenuConexaoId(null)}
              className="w-full text-left px-4 py-2.5 text-xs text-ink-400 hover:bg-paper border-t border-gray-50">
              Cancelar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function PerfilCompleto({ onVoltar }) {
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState(null);
  const [perfil, setPerfil] = useState(null);
  const [stats, setStats] = useState(null);
  const [historicoLeitor, setHistoricoLeitor] = useState([]);
  const [livrosEstante, setLivrosEstante] = useState([]);
  const [avaliacoesComoLeitor, setAvaliacoesComoLeitor] = useState([]);
  const [avaliacoesComoDono, setAvaliacoesComoDono] = useState([]);
  const [livrosAvaliados, setLivrosAvaliados] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState(null);
  const [badges, setBadges] = useState([]);
  const [seguindoList, setSeguindoList] = useState([]);
  const [seguidoresList, setSeguidoresList] = useState([]);
  const [abaConexoes, setAbaConexoes] = useState('seguindo');
  const [seguindoSet, setSeguindoSet] = useState(new Set());
  const [bloqueadosSet, setBloqueadosSet] = useState(new Set());
  const [seguindoCarregandoId, setSeguindoCarregandoId] = useState(null);
  const [buscaConexao, setBuscaConexao] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscandoConexao, setBuscandoConexao] = useState(false);
  const [menuConexaoId, setMenuConexaoId] = useState(null);
  const [badgeAberta, setBadgeAberta] = useState(null);
  const [clubesList, setClubesList] = useState([]);
  const [abaAvaliacao, setAbaAvaliacao] = useState('historico');

  // Edit modal
  const [modalEditar, setModalEditar] = useState(false);
  const [editNome, setEditNome] = useState('');
  const [editUsername, setEditUsername] = useState('');
  const [editUsernameStatus, setEditUsernameStatus] = useState(null);
  const [editFotoUrl, setEditFotoUrl] = useState('');
  const [editBio, setEditBio] = useState('');
  const [editWhatsapp, setEditWhatsapp] = useState('');
  const [uploadingFoto, setUploadingFoto] = useState(false);
  const [salvandoPerfil, setSalvandoPerfil] = useState(false);
  const timerUsername = useRef(null);
  const fotoInputRef = useRef(null);

  // Reporte
  const [modalReporte, setModalReporte] = useState(false);
  const [reporteCategoria, setReporteCategoria] = useState('bug');
  const [reporteTexto, setReporteTexto] = useState('');
  const [enviandoReporte, setEnviandoReporte] = useState(false);

  async function handleEnviarReporte() {
    if (!reporteTexto.trim()) return;
    setEnviandoReporte(true);
    await supabase.from('reportes').insert({
      user_id: usuario?.id || null,
      user_nome: perfil?.nome || null,
      user_email: usuario?.email || null,
      categoria: reporteCategoria,
      texto: reporteTexto.trim(),
    });
    setEnviandoReporte(false);
    setModalReporte(false);
    setReporteTexto('');
    setReporteCategoria('bug');
    showToast('Mensagem enviada. Obrigado!', 'success');
  }

  // Account section
  const [pausando, setPausando] = useState(false);
  const [etapaExcluir, setEtapaExcluir] = useState(null); // null | 'aviso' | 'confirmar'
  const [confirmTexto, setConfirmTexto] = useState('');
  const [excluindo, setExcluindo] = useState(false);
  const [avisosAtivos, setAvisosAtivos] = useState(0);
  const [darkMode, setDarkMode] = useState(() => document.documentElement.classList.contains('dark'));

  function toggleDarkMode() {
    const novo = !darkMode;
    setDarkMode(novo);
    if (novo) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('tema', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('tema', 'light');
    }
  }

  // Username validation for modal
  useEffect(() => {
    if (!modalEditar) return;
    const u = editUsername;
    if (!u) { setEditUsernameStatus(null); return; }
    if (u.length < 3) { setEditUsernameStatus('short'); return; }
    if (!/^[a-z0-9.]+$/.test(u)) { setEditUsernameStatus('invalid'); return; }
    if (u === perfil?.username) { setEditUsernameStatus('same'); return; }
    setEditUsernameStatus('checking');
    clearTimeout(timerUsername.current);
    timerUsername.current = setTimeout(async () => {
      const { data } = await supabase
        .from('perfis').select('id').ilike('username', u).neq('id', usuario?.id).maybeSingle();
      setEditUsernameStatus(data ? 'taken' : 'available');
    }, 400);
    return () => clearTimeout(timerUsername.current);
  }, [editUsername, modalEditar, perfil?.username, usuario?.id]);

  useEffect(() => {
    async function carregar() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUsuario(user);

      const { data: perfilData } = await supabase
        .from('perfis').select('slug, username, foto_url, pausada, whatsapp, bio').eq('id', user.id).maybeSingle();
      setPerfil(perfilData || null);
      const badgesData = await buscarBadges(user.id);
      setBadges(badgesData);

      const { data: seguindoRows } = await supabase
        .from('seguidores').select('seguido_id').eq('seguidor_id', user.id);
      const seguindoIds = (seguindoRows || []).map(r => r.seguido_id);
      if (seguindoIds.length > 0) {
        const { data: seguindoData } = await supabase
          .from('perfis').select('id, nome, username, foto_url, slug').in('id', seguindoIds);
        const lista = seguindoData || [];
        setSeguindoList(lista);
        setSeguindoSet(new Set(lista.map(p => p.id)));
      }

      const { data: seguidoresRows } = await supabase
        .from('seguidores').select('seguidor_id').eq('seguido_id', user.id);
      const seguidoresIds = (seguidoresRows || []).map(r => r.seguidor_id);
      if (seguidoresIds.length > 0) {
        const { data: seguidoresData } = await supabase
          .from('perfis').select('id, nome, username, foto_url, slug').in('id', seguidoresIds);
        setSeguidoresList(seguidoresData || []);
      }

      const { data: bloqueiosData } = await supabase
        .from('bloqueios').select('bloqueado_id').eq('bloqueador_id', user.id);
      if (bloqueiosData) setBloqueadosSet(new Set(bloqueiosData.map(b => b.bloqueado_id)));

      const { data: livros } = await supabase
        .from('livros').select('id, disponivel, titulo, autor, capa_url').eq('user_id', user.id);
      const ids = livros?.map(l => l.id) || [];
      setLivrosEstante(livros || []);

      let emprestimosComoDono = [];
      if (ids.length > 0) {
        const { data } = await supabase
          .from('emprestimos').select('*, livros(titulo, autor, capa_url)')
          .in('livro_id', ids).eq('status', 'devolvido');
        emprestimosComoDono = data || [];
      }

      const { data: emprestimosComoLeitor } = await supabase
        .from('emprestimos').select('*, livros(titulo, autor, capa_url)')
        .eq('leitor_user_id', user.id)
        .order('created_at', { ascending: false });

      const leitorTodos = emprestimosComoLeitor || [];
      const leitorDevolvidos = leitorTodos.filter(e => e.status === 'devolvido');

      setHistoricoLeitor(leitorTodos);
      setAvaliacoesComoLeitor(leitorDevolvidos.filter(e => e.avaliacao_leitor_estrelas || e.avaliacao_leitor_comentario));
      setAvaliacoesComoDono(emprestimosComoDono.filter(e => e.avaliacao_dono_estrelas || e.avaliacao_dono_comentario));
      setLivrosAvaliados(leitorDevolvidos.filter(e => e.leitor_feedback_estrelas || e.leitor_feedback_comentario));
      setStats({
        totalLivros: livros?.length || 0,
        disponiveis: livros?.filter(l => l.disponivel).length || 0,
        livrosLidos: leitorDevolvidos.length,
        emprestimosAtivos: leitorTodos.filter(e => e.status === 'aprovado').length,
      });
      const { data: clubesData } = await supabase
        .from('clube_membros')
        .select('clubes(id, nome, descricao, slug, capa_url)')
        .eq('user_id', user.id)
        .eq('status', 'aprovado');
      setClubesList((clubesData || []).map(m => m.clubes).filter(Boolean));


      setCarregando(false);
    }
    carregar();
  }, []);

  useEffect(() => {
    const q = buscaConexao.replace(/^@/, '').trim();
    if (q.length < 2) { setResultadosBusca([]); setBuscandoConexao(false); return; }
    setBuscandoConexao(true);
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('perfis').select('id, nome, username, foto_url, slug')
        .or(`username.ilike.%${q}%,nome.ilike.%${q}%`)
        .neq('id', usuario?.id || '')
        .limit(8);
      setResultadosBusca(data || []);
      setBuscandoConexao(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [buscaConexao, usuario?.id]);

  async function handleSeguir(alvo) {
    setSeguindoCarregandoId(alvo.id);
    await supabase.from('seguidores').insert({ seguidor_id: usuario.id, seguido_id: alvo.id });
    setSeguindoSet(prev => new Set([...prev, alvo.id]));
    setSeguindoList(prev => [...prev, alvo]);
    setSeguindoCarregandoId(null);
    showToast(`Seguindo ${alvo.nome || 'usuário'}!`, 'success');
    const nomeSelf = usuario.user_metadata?.nome || usuario.email?.split('@')[0] || 'Alguém';
    await supabase.from('notificacoes').insert({
      user_id: alvo.id, tipo: 'novo_seguidor', lida: false,
      meta: { nome: nomeSelf, username: perfil?.username || null, seguidor_id: usuario.id },
    });
  }

  async function handlePararSeguir(alvoId) {
    await supabase.from('seguidores').delete().eq('seguidor_id', usuario.id).eq('seguido_id', alvoId);
    setSeguindoSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setSeguindoList(prev => prev.filter(p => p.id !== alvoId));
    setMenuConexaoId(null);
  }

  async function handleBloquear(alvoId) {
    await supabase.from('seguidores').delete().eq('seguidor_id', usuario.id).eq('seguido_id', alvoId);
    await supabase.from('seguidores').delete().eq('seguidor_id', alvoId).eq('seguido_id', usuario.id);
    await supabase.from('bloqueios').insert({ bloqueador_id: usuario.id, bloqueado_id: alvoId });
    setSeguindoSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setSeguindoList(prev => prev.filter(p => p.id !== alvoId));
    setSeguidoresList(prev => prev.filter(p => p.id !== alvoId));
    setBloqueadosSet(prev => new Set([...prev, alvoId]));
    setMenuConexaoId(null);
    showToast('Usuário bloqueado.', 'info');
  }

  async function handleDesbloquear(alvoId) {
    await supabase.from('bloqueios').delete().eq('bloqueador_id', usuario.id).eq('bloqueado_id', alvoId);
    setBloqueadosSet(prev => { const s = new Set(prev); s.delete(alvoId); return s; });
    setMenuConexaoId(null);
    showToast('Usuário desbloqueado.', 'success');
  }

  const nome = usuario?.user_metadata?.nome || usuario?.email?.split('@')[0] || '';
  const repDono = calcMedia(avaliacoesComoDono, 'avaliacao_dono_estrelas');
  const repLeitor = calcMedia(avaliacoesComoLeitor, 'avaliacao_leitor_estrelas');

  const livrosLidos = (() => {
    const mapa = {};
    historicoLeitor.filter(e => e.status === 'devolvido').forEach(e => {
      if (!mapa[e.livro_id] || e.leitor_feedback_estrelas || e.leitor_feedback_comentario) mapa[e.livro_id] = e;
    });
    return Object.values(mapa);
  })();

  function abrirEditar() {
    setEditNome(nome);
    setEditUsername(perfil?.username || '');
    setEditFotoUrl(perfil?.foto_url || usuario?.user_metadata?.avatar_url || '');
    setEditBio(perfil?.bio || '');
    setEditWhatsapp(perfil?.whatsapp || '');
    setEditUsernameStatus(perfil?.username ? 'same' : null);
    setModalEditar(true);
  }

  async function handleFotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast('A foto deve ter no máximo 10MB.', 'error');
      return;
    }
    setUploadingFoto(true);
    try {
      // Converte qualquer formato (incluindo HEIC do iOS) para JPEG via canvas
      const blob = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = ev => {
          const img = new Image();
          img.onload = () => {
            const MAX = 800;
            let w = img.width, h = img.height;
            if (w > MAX || h > MAX) {
              if (w > h) { h = Math.round(h * MAX / w); w = MAX; }
              else { w = Math.round(w * MAX / h); h = MAX; }
            }
            const canvas = document.createElement('canvas');
            canvas.width = w; canvas.height = h;
            canvas.getContext('2d').drawImage(img, 0, 0, w, h);
            canvas.toBlob(b => b ? resolve(b) : reject(new Error('Conversão falhou')), 'image/jpeg', 0.88);
          };
          img.onerror = () => reject(new Error('Não foi possível ler a imagem'));
          img.src = ev.target.result;
        };
        reader.onerror = () => reject(new Error('Erro ao ler o arquivo'));
        reader.readAsDataURL(file);
      });

      // Path com pasta para respeitar a policy do Supabase Storage
      const path = `${usuario.id}/avatar.jpg`;
      const { data, error } = await supabase.storage
        .from('avatares').upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
      if (error) {
        showToast('Erro no upload: ' + error.message, 'error');
      } else {
        const { data: urlData } = supabase.storage.from('avatares').getPublicUrl(data.path);
        setEditFotoUrl(urlData.publicUrl + `?t=${Date.now()}`);
      }
    } catch (err) {
      showToast('Erro ao processar a imagem: ' + err.message, 'error');
    }
    setUploadingFoto(false);
  }

  async function handleSalvarPerfil() {
    const usernameOk = !editUsername || editUsernameStatus === 'available' || editUsernameStatus === 'same';
    if (!usernameOk) return;
    setSalvandoPerfil(true);
    try {
      const fotoUrlLimpa = editFotoUrl ? editFotoUrl.split('?')[0] : null;
      const wppLimpo = editWhatsapp.replace(/\D/g, '').slice(0, 11) || null;
      await supabase.auth.updateUser({ data: { nome: editNome } });
      const bioLimpa = editBio.trim() || null;
      const { error } = await supabase.from('perfis').update({
        nome: editNome,
        username: editUsername || null,
        foto_url: fotoUrlLimpa || null,
        whatsapp: wppLimpo,
        bio: bioLimpa,
      }).eq('id', usuario.id);
      if (error) throw error;
      setPerfil(prev => ({ ...prev, username: editUsername || null, foto_url: editFotoUrl || null, whatsapp: wppLimpo, bio: bioLimpa }));
      setUsuario(prev => ({ ...prev, user_metadata: { ...prev.user_metadata, nome: editNome } }));
      setModalEditar(false);
      showToast('Perfil salvo com sucesso!', 'success');
    } catch (err) {
      console.error('handleSalvarPerfil:', err);
      showToast('Erro ao salvar perfil. Tente novamente.', 'error');
    } finally {
      setSalvandoPerfil(false);
    }
  }


  async function handlePausarBiblioteca() {
    setPausando(true);
    const novoPausada = !perfil?.pausada;
    await supabase.from('perfis').update({ pausada: novoPausada }).eq('id', usuario.id);
    setPerfil(prev => ({ ...prev, pausada: novoPausada }));
    setPausando(false);
  }

  async function iniciarExcluir() {
    const { data: livros } = await supabase.from('livros').select('id').eq('user_id', usuario.id);
    const livroIds = (livros || []).map(l => l.id);
    const { data: a1 } = await supabase.from('emprestimos').select('id')
      .eq('leitor_user_id', usuario.id).in('status', ['pendente', 'aprovado', 'em_andamento']);
    let a2 = [];
    if (livroIds.length > 0) {
      const { data } = await supabase.from('emprestimos').select('id')
        .in('livro_id', livroIds).in('status', ['pendente', 'aprovado', 'em_andamento']);
      a2 = data || [];
    }
    setAvisosAtivos((a1?.length || 0) + a2.length);
    setEtapaExcluir('aviso');
  }

  async function handleExcluirConta() {
    if (confirmTexto !== 'EXCLUIR') return;
    setExcluindo(true);
    try {
      const res = await fetch('https://bibliotecadebolso.app/.netlify/functions/delete-account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: usuario.id }),
      });
      const data = await res.json();
      if (res.status === 409) {
        setAvisosAtivos(data.count);
        setEtapaExcluir('aviso');
        setConfirmTexto('');
        return;
      }
      if (!res.ok) throw new Error(data.error || 'Erro ao excluir');
      await supabase.auth.signOut();
      window.location.href = '/';
    } catch (err) {
      showToast('Erro ao excluir conta: ' + (err.message || 'tente novamente.'), 'error');
    } finally {
      setExcluindo(false);
    }
  }

  function usernameHintTexto(status) {
    if (status === 'short') return ['text-ink-300', 'mín. 3 caracteres'];
    if (status === 'invalid') return ['text-red-400', 'só letras, números e ponto'];
    if (status === 'checking') return ['text-ink-300', 'verificando…'];
    if (status === 'taken') return ['text-red-400', 'já em uso'];
    if (status === 'available') return ['text-green-500', 'disponível'];
    if (status === 'same') return ['text-ink-300', 'atual'];
    return null;
  }

  const cardShadow = '0 1px 2px rgba(33,28,43,0.04),0 4px 16px rgba(33,28,43,0.05)';
  const publicSlug = perfil?.username ? `@${perfil.username}` : perfil?.slug || '';

  return (
    <div className="bg-paper font-sans flex flex-col" style={{height:'100dvh'}}>

      {/* Mobile layout */}
      <div className="flex-1 overflow-y-auto lg:hidden" style={{scrollbarWidth:'none'}}>

      {/* Back header — sticky frosted */}
      <div className="sticky top-0 z-10 flex items-center gap-3 px-[18px] pb-3"
        style={{paddingTop:'calc(env(safe-area-inset-top) + 14px)', background:'rgba(250,248,245,0.88)', backdropFilter:'blur(14px) saturate(160%)', WebkitBackdropFilter:'blur(14px) saturate(160%)', borderBottom:'1px solid #EBE8F0'}}>
        <button onClick={onVoltar}
          className="w-[38px] h-[38px] rounded-[12px] border border-[#EBE8F0] bg-white flex items-center justify-center text-ink-700 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>
        <h1 className="flex-1 text-[17px] font-extrabold tracking-tight text-ink-900 m-0">Perfil</h1>
        <button onClick={abrirEditar} className="text-[13px] font-bold bg-transparent border-0 cursor-pointer" style={{color:'#5B3CE4'}}>Editar</button>
      </div>

      <div className="max-w-3xl mx-auto pb-16">

        {carregando ? (
          <p className="text-sm text-ink-400 text-center pt-16">Carregando...</p>
        ) : (
          <>
            {/* Hero */}
            <div className="text-center px-5 pt-7 pb-5">
              <div
                className="w-[86px] h-[86px] rounded-[28px] mx-auto mb-3.5 flex items-center justify-center text-[34px] font-extrabold text-white overflow-hidden cursor-pointer"
                style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)', boxShadow:'0 10px 24px rgba(91,60,228,0.32)'}}
                onClick={abrirEditar}>
                {(perfil?.foto_url || usuario?.user_metadata?.avatar_url)
                  ? <img src={perfil?.foto_url || usuario?.user_metadata?.avatar_url} alt="Perfil" className="w-full h-full object-cover" />
                  : nome.charAt(0).toUpperCase() || '?'}
              </div>
              <h1 className="text-[22px] font-extrabold tracking-tight m-0 mb-0.5 text-ink-900">{nome}</h1>
              {perfil?.username && (
                <p className="text-[13.5px] font-bold m-0 mb-2" style={{color:'#5B3CE4'}}>@{perfil.username}</p>
              )}
              {perfil?.bio && (
                <p className="text-[13px] text-ink-500 m-0 mb-3 max-w-xs mx-auto leading-relaxed">{perfil.bio}</p>
              )}
              <div className="flex items-center justify-center gap-2.5 flex-wrap">
                {(repLeitor || repDono) && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full" style={{background:'#FBF1DC'}}>
                    <span style={{color:'#FFAA00', fontSize:15}}>★</span>
                    <span className="text-[13px] font-extrabold" style={{color:'#9A6B12'}}>{repLeitor || repDono}</span>
                    <span className="text-[11px] font-semibold text-ink-400">como {repLeitor ? 'leitor' : 'dono'}</span>
                  </div>
                )}
                {badges.slice(0, 5).map(b => (
                  <span key={b.tipo} className="w-7 h-7 rounded-full flex items-center justify-center" style={{background:'#EDE9FF'}}>
                    <b.Icon size={14} strokeWidth={2} color="#5B3CE4" />
                  </span>
                ))}
                {badges.length > 5 && (
                  <span className="text-[11px] font-bold text-ink-400">+{badges.length - 5}</span>
                )}
              </div>
            </div>

            {/* Banner WhatsApp ausente */}
            {!perfil?.whatsapp && (
              <div className="mx-5 mb-4 px-4 py-3 rounded-[16px] flex items-center gap-3" style={{background:'#FFF7E6',border:'1.5px solid #FFD580'}}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B8860B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <p className="text-[12.5px] text-ink-700 leading-snug m-0 flex-1">Adicione seu WhatsApp para poder fazer e receber empréstimos.</p>
                <button onClick={abrirEditar} className="text-[12px] font-bold border-0 bg-transparent cursor-pointer" style={{color:'#B8860B',whiteSpace:'nowrap'}}>Adicionar</button>
              </div>
            )}

            {/* Stats row — clicável */}
            {stats && (
              <div className="flex bg-white border border-line rounded-[18px] mx-5 mb-5" style={{boxShadow: cardShadow}}>
                {[
                  [stats.totalLivros, 'livros', () => publicSlug && navigate(`/biblioteca/${publicSlug}`)],
                  [seguidoresList.length, 'seguidores', () => { setAba('conexoes'); setAbaConexoes('seguidores'); }],
                  [seguindoList.length, 'seguindo', () => { setAba('conexoes'); setAbaConexoes('seguindo'); }],
                ].map(([n, l, onClick], i) => (
                  <button key={i} onClick={onClick}
                    className="flex-1 text-center py-4 relative border-0 bg-transparent cursor-pointer">
                    {i > 0 && <div className="absolute left-0 top-[22%] bottom-[22%] w-px bg-line" />}
                    <div className="text-[20px] font-extrabold tracking-tight text-ink-900">{n}</div>
                    <div className="text-[11.5px] text-ink-400 font-semibold mt-0.5">{l}</div>
                  </button>
                ))}
              </div>
            )}

            {/* Conexões view (abre ao clicar em seguidores/seguindo) */}
            {aba === 'conexoes' && (
              <div className="px-5 mb-5">
                <button onClick={() => setAba(null)}
                  className="flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-400 mb-4 bg-transparent border-0 cursor-pointer">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Voltar
                </button>
                <div className="relative mb-3">
                  <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input type="text" placeholder="Buscar por @usuário ou nome..." value={buscaConexao}
                    onChange={e => setBuscaConexao(e.target.value)}
                    className="w-full bg-white border border-line rounded-[13px] pl-8 pr-8 py-2.5 text-xs outline-none text-ink-900" />
                  {buscaConexao && (
                    <button onClick={() => { setBuscaConexao(''); setResultadosBusca([]); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-300">
                      <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                </div>
                {buscaConexao.trim() ? (
                  <div>
                    {buscandoConexao ? <p className="text-xs text-ink-400 text-center py-5">Buscando...</p>
                      : resultadosBusca.length === 0 ? <p className="text-xs text-ink-400 text-center py-5">Nenhum usuário encontrado</p>
                      : <div className="flex flex-col gap-2">{resultadosBusca.map(p => (
                          <ConexaoCard key={p.id} p={p} seguindoSet={seguindoSet} bloqueadosSet={bloqueadosSet}
                            menuConexaoId={menuConexaoId} setMenuConexaoId={setMenuConexaoId}
                            onSeguir={handleSeguir} onPararSeguir={handlePararSeguir}
                            onBloquear={handleBloquear} onDesbloquear={handleDesbloquear}
                            navigate={navigate} seguindoCarregandoId={seguindoCarregandoId} />
                        ))}</div>}
                  </div>
                ) : (
                  <>
                    <div className="flex bg-paper-2 rounded-[13px] p-1 mb-3 gap-0.5">
                      <button onClick={() => setAbaConexoes('seguindo')}
                        className={`flex-1 py-2 text-xs rounded-[10px] transition-all font-medium ${abaConexoes === 'seguindo' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-400'}`}>
                        Seguindo ({seguindoList.length})
                      </button>
                      <button onClick={() => setAbaConexoes('seguidores')}
                        className={`flex-1 py-2 text-xs rounded-[10px] transition-all font-medium ${abaConexoes === 'seguidores' ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-400'}`}>
                        Seguidores ({seguidoresList.length})
                      </button>
                    </div>
                    {(abaConexoes === 'seguindo' ? seguindoList : seguidoresList).length === 0 ? (
                      <div className="text-center py-8">
                        <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}>
                          <Users size={20} strokeWidth={1.6} color="#5B3CE4" />
                        </div>
                        <p className="text-[13px] text-ink-400">
                          {abaConexoes === 'seguindo' ? 'Você ainda não segue ninguém' : 'Ninguém te segue ainda'}
                        </p>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        {(abaConexoes === 'seguindo' ? seguindoList : seguidoresList).map(p => (
                          <ConexaoCard key={p.id} p={p} seguindoSet={seguindoSet} bloqueadosSet={bloqueadosSet}
                            menuConexaoId={menuConexaoId} setMenuConexaoId={setMenuConexaoId}
                            onSeguir={handleSeguir} onPararSeguir={handlePararSeguir}
                            onBloquear={handleBloquear} onDesbloquear={handleDesbloquear}
                            navigate={navigate} seguindoCarregandoId={seguindoCarregandoId} />
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Accordion + conta (oculto quando conexoes está aberto) */}
            {aba !== 'conexoes' && (
              <>
                {/* Premium upgrade button */}
                {!isPago && (
                  <div className="mx-5 mb-4">
                    <button onClick={openUpgrade}
                      className="w-full rounded-[18px] p-4 flex items-center gap-3 border-0 cursor-pointer text-left"
                      style={{background:'linear-gradient(150deg,#6E50F0,#3F27B0)'}}>
                      <div className="w-10 h-10 rounded-[13px] flex items-center justify-center flex-shrink-0" style={{background:'rgba(255,255,255,0.18)'}}>
                        <Sparkles size={20} color="white" strokeWidth={1.8} />
                      </div>
                      <div className="flex-1">
                        <p className="text-[14px] font-extrabold text-white m-0 leading-tight">Seja Premium</p>
                        <p className="text-[12px] m-0 mt-0.5" style={{color:'rgba(255,255,255,0.72)'}}>Livros ilimitados, clubes e muito mais</p>
                      </div>
                      <ChevronRight size={18} color="white" strokeWidth={2.2} />
                    </button>
                  </div>
                )}

                {/* Accordion card */}
                <div className="mx-5 mb-4 bg-white border border-line rounded-[18px] overflow-hidden" style={{boxShadow: cardShadow}}>

                  {/* Conquistas */}
                  <button onClick={() => setAba(aba === 'conquistas' ? null : 'conquistas')}
                    className="w-full flex items-center gap-3 px-4 py-[15px] bg-transparent border-0 cursor-pointer text-left">
                    <Trophy size={18} strokeWidth={1.8} color="#5B3CE4" />
                    <span className="flex-1 text-[14.5px] font-semibold text-ink-900">Conquistas</span>
                    <span className="flex items-center gap-0.5 mr-1">
                      {badges.slice(0,3).map(b => <b.Icon key={b.tipo} size={13} strokeWidth={2} color="#5B3CE4" />)}
                      {badges.length > 3 && <span className="text-[11px] font-bold text-ink-400 ml-0.5">+{badges.length-3}</span>}
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      {aba === 'conquistas' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                    </svg>
                  </button>
                  {aba === 'conquistas' && (
                    <div style={{borderTop:'1px solid #F2EEE9'}} className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-[11.5px] font-extrabold uppercase tracking-[0.05em] text-ink-400 m-0">Todas as conquistas</p>
                        <p className="text-[11.5px] text-ink-400 m-0">{badges.length}/{BADGE_DEFS.length}</p>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {BADGE_DEFS.map(def => {
                          const earned = badges.some(b => b.tipo === def.tipo);
                          const isOpen = badgeAberta === def.tipo;
                          return (
                            <button key={def.tipo}
                              onClick={() => setBadgeAberta(isOpen ? null : def.tipo)}
                              className={`bg-white border rounded-[18px] p-4 text-left transition-all ${earned ? def.cor : 'border-line opacity-55'}`}>
                              <div className={`w-11 h-11 rounded-[13px] flex items-center justify-center mb-2.5 border ${earned ? def.cor : 'bg-paper border-line'}`}>
                                <def.Icon size={22} strokeWidth={earned ? 1.8 : 1.4} />
                              </div>
                              <p className="text-[12.5px] font-semibold text-ink-900 leading-snug m-0">{def.nome}</p>
                              {earned
                                ? <p className="text-xs mt-1 text-success">desbloqueada</p>
                                : <p className="text-xs text-ink-400 mt-1.5 leading-relaxed">{def.desc}</p>
                              }
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Avaliações */}
                  <button onClick={() => setAba(aba === 'avaliacoes' ? null : 'avaliacoes')}
                    className="w-full flex items-center gap-3 px-4 py-[15px] bg-transparent border-0 cursor-pointer text-left"
                    style={{borderTop:'1px solid #F2EEE9'}}>
                    <Star size={18} strokeWidth={1.8} color="#FFAA00" />
                    <span className="flex-1 text-[14.5px] font-semibold text-ink-900">Avaliações</span>
                    {repLeitor && <span className="text-[12px] font-bold mr-1" style={{color:'#9A6B12'}}>{repLeitor} ★</span>}
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      {aba === 'avaliacoes' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                    </svg>
                  </button>
                  {aba === 'avaliacoes' && (
                    <div style={{borderTop:'1px solid #F2EEE9'}} className="pb-3">
                      <div className="flex bg-paper-2 rounded-[11px] p-[3px] gap-0.5 mx-4 mt-3 mb-3">
                        {[
                          ['historico', 'Histórico'],
                          ['leitor', 'Como leitor'],
                          ['dono', 'Como dono'],
                          ['avaliados', 'Livros avaliados'],
                        ].map(([k, l]) => (
                          <button key={k} onClick={() => setAbaAvaliacao(k)}
                            className="flex-1 py-1.5 text-[9.5px] rounded-[9px] border-0 cursor-pointer font-semibold leading-tight transition-all"
                            style={abaAvaliacao === k ? {background:'#fff', color:'#211C2B', boxShadow:'0 1px 3px rgba(0,0,0,0.08)'} : {background:'transparent', color:'#9A93A4'}}>
                            {l}
                          </button>
                        ))}
                      </div>
                      <div className="px-4">
                        {abaAvaliacao === 'historico' && (
                          historicoLeitor.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}><BookOpen size={20} strokeWidth={1.6} color="#5B3CE4" /></div>
                              <p className="text-[13px] text-ink-400">Nenhum empréstimo ainda</p>
                            </div>
                          ) : historicoLeitor.map(e => (
                            <div key={e.id} className="bg-white border border-line rounded-[14px] p-3.5 mb-2.5" style={{boxShadow: cardShadow}}>
                              <div className="flex items-center gap-3">
                                <div className="w-7 h-10 bg-paper-2 rounded-[6px] flex items-center justify-center flex-shrink-0"><BookOpen size={14} strokeWidth={1.6} color="#9A93A4" /></div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12.5px] font-semibold text-ink-900 truncate m-0" style={{fontFamily:'Newsreader,Georgia,serif'}}>{e.livros?.titulo}</p>
                                  <p className="text-[11px] text-ink-400 mt-0.5 m-0">{e.livros?.autor}</p>
                                </div>
                                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full flex-shrink-0"
                                  style={e.status === 'aprovado' ? {background:'#E4F6EE', color:'#1FA971'} : e.status === 'pendente' ? {background:'#FBF1DC', color:'#9A6B12'} : {background:'#F2F0F5', color:'#9A93A4'}}>
                                  {e.status === 'devolvido' ? 'Devolvido' : e.status === 'aprovado' ? 'Em mãos' : e.status === 'pendente' ? 'Pendente' : 'Recusado'}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                        {abaAvaliacao === 'leitor' && (
                          avaliacoesComoLeitor.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}><Star size={20} strokeWidth={1.6} color="#5B3CE4" /></div>
                              <p className="text-[13px] text-ink-400">Nenhuma avaliação ainda</p>
                            </div>
                          ) : (
                            <>
                              <MediaDestaque lista={avaliacoesComoLeitor} campo="avaliacao_leitor_estrelas" />
                              {avaliacoesComoLeitor.map(e => (
                                <div key={e.id} className="bg-white border border-line rounded-[14px] p-3.5 mb-2.5" style={{boxShadow: cardShadow}}>
                                  <p className="text-[12.5px] font-semibold text-ink-900 truncate m-0 mb-0.5" style={{fontFamily:'Newsreader,Georgia,serif'}}>{e.livros?.titulo}</p>
                                  <p className="text-[11px] text-ink-400 m-0 mb-1.5">{e.livros?.autor}</p>
                                  {e.avaliacao_leitor_estrelas > 0 && <Estrelas n={e.avaliacao_leitor_estrelas} />}
                                  {e.avaliacao_leitor_comentario && <p className="text-xs text-ink-500 italic mt-1.5 m-0">"{e.avaliacao_leitor_comentario}"</p>}
                                </div>
                              ))}
                            </>
                          )
                        )}
                        {abaAvaliacao === 'dono' && (
                          avaliacoesComoDono.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}><Star size={20} strokeWidth={1.6} color="#5B3CE4" /></div>
                              <p className="text-[13px] text-ink-400">Nenhuma avaliação ainda</p>
                            </div>
                          ) : (
                            <>
                              <MediaDestaque lista={avaliacoesComoDono} campo="avaliacao_dono_estrelas" />
                              {avaliacoesComoDono.map(e => (
                                <div key={e.id} className="bg-white border border-line rounded-[14px] p-3.5 mb-2.5" style={{boxShadow: cardShadow}}>
                                  <p className="text-[12.5px] font-semibold text-ink-900 truncate m-0 mb-0.5" style={{fontFamily:'Newsreader,Georgia,serif'}}>{e.livros?.titulo}</p>
                                  <p className="text-[11px] text-ink-400 m-0 mb-1.5">{e.leitor_nome}</p>
                                  {e.avaliacao_dono_estrelas > 0 && <Estrelas n={e.avaliacao_dono_estrelas} />}
                                  {e.avaliacao_dono_comentario && <p className="text-xs text-ink-500 italic mt-1.5 m-0">"{e.avaliacao_dono_comentario}"</p>}
                                </div>
                              ))}
                            </>
                          )
                        )}
                        {abaAvaliacao === 'avaliados' && (
                          livrosAvaliados.length === 0 ? (
                            <div className="text-center py-8">
                              <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}><BookMarked size={20} strokeWidth={1.6} color="#5B3CE4" /></div>
                              <p className="text-[13px] text-ink-400">Nenhum livro avaliado ainda</p>
                            </div>
                          ) : livrosAvaliados.map(e => (
                            <div key={e.id} className="bg-white border border-line rounded-[14px] p-3.5 mb-2.5" style={{boxShadow: cardShadow}}>
                              <p className="text-[12.5px] font-semibold text-ink-900 truncate m-0 mb-0.5" style={{fontFamily:'Newsreader,Georgia,serif'}}>{e.livros?.titulo}</p>
                              <p className="text-[11px] text-ink-400 m-0 mb-1.5">{e.livros?.autor}</p>
                              {e.leitor_feedback_estrelas > 0 && <Estrelas n={e.leitor_feedback_estrelas} />}
                              {e.leitor_feedback_comentario && <p className="text-xs text-ink-500 italic mt-1.5 m-0">"{e.leitor_feedback_comentario}"</p>}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}

                  {/* Clubes do Livro */}
                  <button onClick={() => setAba(aba === 'clubes' ? null : 'clubes')}
                    className="w-full flex items-center gap-3 px-4 py-[15px] bg-transparent border-0 cursor-pointer text-left"
                    style={{borderTop:'1px solid #F2EEE9'}}>
                    <Building2 size={18} strokeWidth={1.8} color="#5B3CE4" />
                    <span className="flex-1 text-[14.5px] font-semibold text-ink-900">Clubes do Livro</span>
                    <span className="text-[12px] text-ink-400 font-semibold mr-1">{clubesList.length}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      {aba === 'clubes' ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                    </svg>
                  </button>
                  {aba === 'clubes' && (
                    <div style={{borderTop:'1px solid #F2EEE9'}}>
                      {clubesList.length === 0 ? (
                        <div className="py-8 text-center">
                          <div className="w-10 h-10 rounded-[13px] flex items-center justify-center mx-auto mb-2" style={{background:'#EDE9FF'}}>
                            <Building2 size={20} strokeWidth={1.6} color="#5B3CE4" />
                          </div>
                          <p className="text-[13px] text-ink-400">Nenhum clube ainda</p>
                        </div>
                      ) : (
                        <div className="p-4 flex flex-col gap-3">
                          {clubesList.map(clube => (
                            <div key={clube.id} onClick={() => navigate(`/clube/${clube.slug}`)}
                              className="flex items-center gap-3 cursor-pointer">
                              <div className="w-10 h-10 rounded-[12px] flex items-center justify-center overflow-hidden flex-shrink-0"
                                style={{background:'#EDE8FC'}}>
                                {clube.capa_url
                                  ? <img src={clube.capa_url} alt="" className="w-full h-full object-cover" />
                                  : <Building2 size={18} strokeWidth={1.6} color="#5B3CE4" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[13.5px] font-semibold text-ink-900 m-0 truncate">{clube.nome}</p>
                                {clube.descricao && <p className="text-[11.5px] text-ink-400 m-0 truncate">{clube.descricao}</p>}
                              </div>
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#BDB7C5" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                </div>

                {/* Estante pública */}
                <div className="mx-5 mb-5">
                  <div className="rounded-[18px] p-4 flex items-center gap-[13px] cursor-pointer"
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/biblioteca/${publicSlug}`); showToast('Link copiado!', 'success'); }}
                    style={{background:'linear-gradient(120deg,#5B3CE4,#3F27B0)'}}>
                    <div className="w-10 h-10 rounded-[12px] flex items-center justify-center flex-shrink-0"
                      style={{background:'rgba(255,255,255,0.18)'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-extrabold text-white m-0">Sua estante pública</p>
                      <p className="text-[12px] text-white/80 m-0 truncate mt-0.5">{publicSlug || usuario?.email}</p>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                  </div>
                </div>


                {/* Conta */}
                <div className="px-5 mb-6">
                  <p className="text-[11.5px] font-extrabold uppercase tracking-[0.05em] text-ink-400 m-0 mb-3">Conta</p>
                  <div className="bg-white border border-line rounded-[18px] overflow-hidden mb-3" style={{boxShadow: cardShadow}}>
                    <div className="flex items-center justify-between px-4 py-[15px] border-b border-line-soft">
                      <div className="flex items-center gap-3">
                        {darkMode
                          ? <Moon size={16} strokeWidth={1.8} color="#5B3CE4" />
                          : <Sun size={16} strokeWidth={1.8} color="#9A93A4" />}
                        <div>
                          <p className="text-[14px] font-semibold text-ink-900 m-0">Modo escuro</p>
                          <p className="text-[12px] text-ink-400 m-0 mt-0.5">Salva automaticamente</p>
                        </div>
                      </div>
                      <button onClick={toggleDarkMode}
                        className="relative w-11 h-6 rounded-full flex-shrink-0 border-0 cursor-pointer outline-none"
                        style={{background: darkMode ? '#5B3CE4' : '#D4CFE2', transition:'background 0.2s', WebkitTapHighlightColor:'transparent'}}>
                        <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
                          style={{left: darkMode ? '22px' : '2px', transition:'left 0.2s'}} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between px-4 py-[15px]">
                      <div>
                        <p className="text-[14px] font-semibold text-ink-900 m-0">Biblioteca privada</p>
                        <p className="text-[12px] text-ink-400 m-0 mt-0.5">Oculta sua conta dos demais usuários</p>
                      </div>
                      <button onClick={handlePausarBiblioteca} disabled={pausando}
                        className="relative w-11 h-6 rounded-full flex-shrink-0 border-0 cursor-pointer outline-none"
                        style={{background: perfil?.pausada ? '#211C2B' : '#D4CFE2', transition:'background 0.2s', WebkitTapHighlightColor:'transparent'}}>
                        <span className="absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm"
                          style={{left: perfil?.pausada ? '22px' : '2px', transition:'left 0.2s'}} />
                      </button>
                    </div>
                  </div>
                  <button onClick={() => setModalReporte(true)}
                    className="w-full py-3 text-[14px] font-semibold rounded-[14px] bg-paper-2 border-0 cursor-pointer flex items-center justify-center gap-2 mb-3"
                    style={{color:'#6E6878'}}>
                    Problemas ou sugestões
                  </button>
                  <button onClick={() => supabase.auth.signOut()}
                    className="w-full py-3 text-[14px] font-bold rounded-[14px] bg-paper-2 border-0 cursor-pointer flex items-center justify-center gap-2 mb-3"
                    style={{color:'#FF7A59'}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#FF7A59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                    Sair da conta
                  </button>
                  <button onClick={iniciarExcluir}
                    className="w-full py-3 text-[13px] font-semibold rounded-[14px] border border-transparent bg-transparent cursor-pointer"
                    style={{color:'#EF4444'}}>
                    Excluir conta
                  </button>
                </div>

              </>
            )}
          </>
        )}
      </div>
      </div>{/* end mobile wrapper */}

      {/* Desktop layout */}
      <div className="hidden lg:flex flex-col flex-1 overflow-y-auto" style={{scrollbarWidth:'none'}}>
        {carregando ? (
          <p style={{textAlign:'center',paddingTop:60,color:'#9A93A4'}}>Carregando...</p>
        ) : (
          <>
            {/* Purple banner */}
            <div style={{height:180,background:'linear-gradient(135deg,#6E50F0,#3F27B0)',flexShrink:0,position:'relative'}}>
              <div style={{position:'absolute',top:20,right:40,display:'flex',gap:12}}>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin + (perfil?.slug ? `/biblioteca/${perfil.slug}` : '')).then(() => showToast('Link copiado!', 'success')).catch(() => showToast('Erro ao copiar link.', 'error')); }}
                  style={{padding:'10px 20px',borderRadius:999,background:'rgba(255,255,255,0.15)',color:'#fff',fontWeight:700,fontSize:13,border:'1.5px solid rgba(255,255,255,0.3)',cursor:'pointer'}}>
                  Compartilhar
                </button>
                <button onClick={abrirEditar}
                  style={{padding:'10px 20px',borderRadius:999,background:'#fff',color:'#5B3CE4',fontWeight:700,fontSize:13,border:'none',cursor:'pointer'}}>
                  Editar perfil
                </button>
              </div>
              {/* Avatar overlapping banner */}
              <div style={{position:'absolute',bottom:-44,left:40,width:88,height:88,borderRadius:'50%',border:'4px solid #fff',overflow:'hidden',background:'linear-gradient(150deg,#6E50F0,#3F27B0)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:32,fontWeight:800,color:'#fff',cursor:'pointer'}}
                onClick={abrirEditar}>
                {(perfil?.foto_url || usuario?.user_metadata?.avatar_url)
                  ? <img src={perfil?.foto_url || usuario?.user_metadata?.avatar_url} alt="Perfil" style={{width:'100%',height:'100%',objectFit:'cover'}} />
                  : nome.charAt(0).toUpperCase()||'?'}
              </div>
            </div>

            {/* Profile info + content */}
            <div style={{padding:'60px 40px 0',display:'flex',gap:32,flex:1}}>
              <div style={{flex:1,minWidth:0}}>
                <h1 style={{fontSize:28,fontWeight:800,color:'#211C2B',margin:'0 0 2px',letterSpacing:'-.02em'}}>{nome}</h1>
                {perfil?.username && <p style={{fontSize:14,color:'#5B3CE4',fontWeight:700,margin:'0 0 8px'}}>@{perfil.username}</p>}
                {perfil?.bio && <p style={{fontSize:14,color:'#6E6878',margin:'0 0 16px',lineHeight:1.5,maxWidth:480}}>{perfil.bio}</p>}

                {/* Stats */}
                <div style={{display:'flex',gap:28,margin:'0 0 24px',flexWrap:'wrap'}}>
                  {[
                    [seguidoresList.length,'seguidores'],
                    [seguindoList.length,'seguindo'],
                    [stats?.totalLivros||0,'livros'],
                    [stats?.livrosLidos||0,'lidos'],
                  ].map(([n,l]) => (
                    <div key={l}>
                      <strong style={{fontSize:20,fontWeight:800,color:'#211C2B'}}>{n}</strong>
                      <span style={{fontSize:13,color:'#9A93A4',marginLeft:4}}>{l}</span>
                    </div>
                  ))}
                </div>

                {/* Banner WhatsApp ausente — desktop */}
                {!perfil?.whatsapp && (
                  <div style={{display:'flex',alignItems:'center',gap:12,background:'#FFF7E6',border:'1.5px solid #FFD580',borderRadius:14,padding:'12px 16px',marginBottom:20}}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#B8860B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{flexShrink:0}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                    <p style={{margin:0,flex:1,fontSize:13,color:'#4B3A00',lineHeight:1.4}}>Adicione seu WhatsApp para poder fazer e receber empréstimos.</p>
                    <button onClick={abrirEditar} style={{border:'none',background:'transparent',cursor:'pointer',fontWeight:700,fontSize:13,color:'#B8860B',whiteSpace:'nowrap'}}>Adicionar</button>
                  </div>
                )}

                {/* Tabs */}
                <div style={{display:'flex',gap:4,borderBottom:'2px solid #EDE9F5',marginBottom:24}}>
                  {[['estante','Estante pública'],['lidos','Lidos'],['avaliacoes','Avaliações']].map(([k,l]) => (
                    <button key={k} onClick={() => setAba(k)}
                      style={{padding:'8px 20px',border:'none',background:'transparent',cursor:'pointer',fontWeight:700,fontSize:14,color:aba===k?'#5B3CE4':'#9A93A4',borderBottom:aba===k?'2px solid #5B3CE4':'2px solid transparent',marginBottom:-2}}>
                      {l}
                    </button>
                  ))}
                </div>

                {/* Book grid */}
                {(aba === 'estante' || !aba) && (
                  livrosEstante.length === 0
                    ? <p style={{textAlign:'center',color:'#9A93A4',paddingTop:32}}>Nenhum livro cadastrado ainda</p>
                    : <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:16,paddingBottom:40}}>
                        {livrosEstante.map(livro => (
                          <div key={livro.id} style={{aspectRatio:'2/3',borderRadius:12,overflow:'hidden',position:'relative',background:'linear-gradient(160deg,#3D3068,#1E1A35)'}}>
                            {livro.capa_url && <img src={livro.capa_url} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />}
                            <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'flex-end',padding:'8px 10px',background:'linear-gradient(0deg,rgba(0,0,0,0.72),transparent)'}}>
                              <p style={{color:'#fff',fontWeight:700,fontSize:12,margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{livro.titulo}</p>
                              <p style={{color:'rgba(255,255,255,0.65)',fontSize:10,margin:0}}>{livro.autor}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                )}

                {aba === 'lidos' && (
                  livrosLidos.length === 0
                    ? <p style={{textAlign:'center',color:'#9A93A4',paddingTop:32}}>Nenhum livro devolvido ainda</p>
                    : <div style={{display:'flex',flexDirection:'column',gap:12,paddingBottom:40}}>
                        {livrosLidos.map(emp => {
                          const livro = emp.livros;
                          if (!livro) return null;
                          return (
                            <div key={emp.id} style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',background:'#fff',borderRadius:14,boxShadow:'0 1px 4px rgba(33,28,43,0.07)'}}>
                              <div style={{width:44,height:60,borderRadius:8,overflow:'hidden',background:'linear-gradient(160deg,#3D3068,#1E1A35)',flexShrink:0,position:'relative'}}>
                                {livro.capa_url && <img src={livro.capa_url} alt="" style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}} />}
                              </div>
                              <div style={{flex:1,minWidth:0}}>
                                <p style={{fontWeight:700,fontSize:14,color:'#211C2B',margin:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{livro.titulo}</p>
                                <p style={{fontSize:12,color:'#9A93A4',margin:'2px 0 0'}}>{livro.autor}</p>
                                {emp.leitor_feedback_estrelas > 0 && <Estrelas n={emp.leitor_feedback_estrelas} />}
                                {emp.leitor_feedback_comentario && <p style={{fontSize:12,color:'#6E6878',fontStyle:'italic',margin:'4px 0 0'}}>"{emp.leitor_feedback_comentario}"</p>}
                              </div>
                            </div>
                          );
                        }).filter(Boolean)}
                      </div>
                )}

                {aba === 'avaliacoes' && (
                  avaliacoesComoLeitor.length === 0 && avaliacoesComoDono.length === 0
                    ? <p style={{textAlign:'center',color:'#9A93A4',paddingTop:32}}>Nenhuma avaliação ainda</p>
                    : <div style={{paddingBottom:40}}>
                        {avaliacoesComoLeitor.length > 0 && (
                          <div style={{marginBottom:28}}>
                            <p style={{fontWeight:800,fontSize:11,color:'#9A93A4',letterSpacing:1,textTransform:'uppercase',margin:'0 0 12px'}}>Como leitor</p>
                            <MediaDestaque lista={avaliacoesComoLeitor} campo="avaliacao_leitor_estrelas" />
                            {avaliacoesComoLeitor.map(e => (
                              <div key={e.id} style={{padding:'12px 0',borderBottom:'1px solid #F2EEE9'}}>
                                <p style={{fontWeight:700,fontSize:13,color:'#211C2B',margin:'0 0 4px'}}>{e.livros?.titulo}</p>
                                {e.avaliacao_leitor_estrelas > 0 && <Estrelas n={e.avaliacao_leitor_estrelas} />}
                                {e.avaliacao_leitor_comentario && <p style={{fontSize:12,color:'#6E6878',fontStyle:'italic',margin:'4px 0 0'}}>"{e.avaliacao_leitor_comentario}"</p>}
                              </div>
                            ))}
                          </div>
                        )}
                        {avaliacoesComoDono.length > 0 && (
                          <div>
                            <p style={{fontWeight:800,fontSize:11,color:'#9A93A4',letterSpacing:1,textTransform:'uppercase',margin:'0 0 12px'}}>Como dono</p>
                            <MediaDestaque lista={avaliacoesComoDono} campo="avaliacao_dono_estrelas" />
                            {avaliacoesComoDono.map(e => (
                              <div key={e.id} style={{padding:'12px 0',borderBottom:'1px solid #F2EEE9'}}>
                                <p style={{fontWeight:700,fontSize:13,color:'#211C2B',margin:'0 0 4px'}}>{e.livros?.titulo}</p>
                                {e.avaliacao_dono_estrelas > 0 && <Estrelas n={e.avaliacao_dono_estrelas} />}
                                {e.avaliacao_dono_comentario && <p style={{fontSize:12,color:'#6E6878',fontStyle:'italic',margin:'4px 0 0'}}>"{e.avaliacao_dono_comentario}"</p>}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                )}
              </div>

              {/* Right column: Conquistas + Endereços */}
              <div style={{width:260,flexShrink:0,alignSelf:'flex-start',display:'flex',flexDirection:'column',gap:16}}>
                {/* Conquistas panel */}
                <div style={{background:'#fff',borderRadius:18,padding:'20px',boxShadow:'0 1px 4px rgba(33,28,43,0.07)'}}>
                  <h3 style={{fontSize:16,fontWeight:800,color:'#211C2B',margin:'0 0 16px'}}>Conquistas</h3>
                  {BADGE_DEFS.map((def, i) => {
                    const earned = badges.some(b => b.tipo === def.tipo);
                    return (
                      <div key={def.tipo} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 0',borderBottom:i<BADGE_DEFS.length-1?'1px solid #F2EEE9':'none',opacity:earned?1:0.4}}>
                        <div style={{width:40,height:40,borderRadius:12,background:'#EDE9FF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                          <def.Icon size={18} strokeWidth={2} color="#5B3CE4" />
                        </div>
                        <div>
                          <p style={{fontWeight:700,fontSize:13,color:'#211C2B',margin:0}}>{def.nome}</p>
                          <p style={{fontSize:11,color:'#9A93A4',margin:0}}>{def.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Conta */}
                <div style={{background:'#fff',borderRadius:18,overflow:'hidden',boxShadow:'0 1px 4px rgba(33,28,43,0.07)'}}>
                  <p style={{fontSize:11,fontWeight:800,color:'#9A93A4',letterSpacing:1,textTransform:'uppercase',margin:'0',padding:'16px 20px 12px'}}>Conta</p>

                  {/* Modo escuro */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderTop:'1px solid #F2EEE9'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      {darkMode
                        ? <Moon size={15} strokeWidth={1.8} color="#5B3CE4" />
                        : <Sun size={15} strokeWidth={1.8} color="#9A93A4" />}
                      <div>
                        <p style={{fontSize:13,fontWeight:600,color:'#211C2B',margin:0}}>Modo escuro</p>
                      </div>
                    </div>
                    <button onClick={toggleDarkMode}
                      style={{position:'relative',width:40,height:22,borderRadius:999,border:'none',cursor:'pointer',background:darkMode?'#5B3CE4':'#D4CFE2',transition:'background 0.2s',flexShrink:0,outline:'none'}}>
                      <span style={{position:'absolute',top:2,width:18,height:18,background:'#fff',borderRadius:'50%',boxShadow:'0 1px 3px rgba(0,0,0,0.15)',left:darkMode?'20px':'2px',transition:'left 0.2s'}} />
                    </button>
                  </div>

                  {/* Biblioteca privada */}
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'12px 20px',borderTop:'1px solid #F2EEE9'}}>
                    <div>
                      <p style={{fontSize:13,fontWeight:600,color:'#211C2B',margin:0}}>Biblioteca privada</p>
                      <p style={{fontSize:11,color:'#9A93A4',margin:'2px 0 0'}}>Oculta dos demais usuários</p>
                    </div>
                    <button onClick={handlePausarBiblioteca} disabled={pausando}
                      style={{position:'relative',width:40,height:22,borderRadius:999,border:'none',cursor:'pointer',background:perfil?.pausada?'#211C2B':'#D4CFE2',transition:'background 0.2s',flexShrink:0,outline:'none'}}>
                      <span style={{position:'absolute',top:2,width:18,height:18,background:'#fff',borderRadius:'50%',boxShadow:'0 1px 3px rgba(0,0,0,0.15)',left:perfil?.pausada?'20px':'2px',transition:'left 0.2s'}} />
                    </button>
                  </div>

                  {/* Problemas ou sugestões */}
                  <div style={{borderTop:'1px solid #F2EEE9'}}>
                    <button onClick={() => setModalReporte(true)}
                      style={{width:'100%',padding:'13px 20px',background:'transparent',color:'#6E6878',fontWeight:600,fontSize:13,border:'none',cursor:'pointer',textAlign:'left'}}>
                      Problemas ou sugestões
                    </button>
                  </div>

                  {/* Sair da conta */}
                  <div style={{borderTop:'1px solid #F2EEE9'}}>
                    <button onClick={() => supabase.auth.signOut()}
                      style={{width:'100%',padding:'13px 20px',background:'transparent',color:'#FF7A59',fontWeight:700,fontSize:13,border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:10,textAlign:'left'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF7A59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                      Sair da conta
                    </button>
                  </div>

                  {/* Excluir conta */}
                  <div style={{borderTop:'1px solid #F2EEE9'}}>
                    <button onClick={iniciarExcluir}
                      style={{width:'100%',padding:'13px 20px',background:'transparent',color:'#EF4444',fontWeight:600,fontSize:13,border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:10,textAlign:'left'}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                      Excluir conta
                    </button>
                  </div>
                </div>

              </div>
            </div>
          </>
        )}
      </div>

      {/* MODAL PROBLEMAS OU SUGESTÕES */}
      {modalReporte && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl p-5">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-semibold text-ink-900">Problemas ou sugestões</p>
              <button onClick={() => setModalReporte(false)}>
                <X size={18} strokeWidth={2} color="#9A93A4" />
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              {[['bug', 'Problema'], ['sugestao', 'Sugestão'], ['outro', 'Outro']].map(([val, label]) => (
                <button key={val} onClick={() => setReporteCategoria(val)}
                  className="flex-1 py-2 text-xs font-semibold rounded-xl border cursor-pointer transition-colors"
                  style={{
                    background: reporteCategoria === val ? '#5B3CE4' : 'transparent',
                    color: reporteCategoria === val ? '#fff' : '#6E6878',
                    borderColor: reporteCategoria === val ? '#5B3CE4' : '#D4CFE2',
                  }}>
                  {label}
                </button>
              ))}
            </div>

            <textarea
              value={reporteTexto}
              onChange={e => setReporteTexto(e.target.value)}
              placeholder={reporteCategoria === 'bug' ? 'Descreva o problema que encontrou...' : reporteCategoria === 'sugestao' ? 'Qual melhoria você sugere?' : 'Deixe sua mensagem...'}
              rows={5}
              className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 outline-none focus:border-line resize-none mb-4 bg-paper"
            />

            <button onClick={handleEnviarReporte} disabled={!reporteTexto.trim() || enviandoReporte}
              className="w-full py-3 rounded-xl text-sm font-bold text-white border-0 cursor-pointer disabled:opacity-50"
              style={{background:'#5B3CE4'}}>
              {enviandoReporte ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      )}

      {/* MODAL EDITAR PERFIL */}
      {modalEditar && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white w-full max-w-md rounded-t-2xl p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm font-medium text-ink-900">Editar perfil</p>
              <button onClick={() => setModalEditar(false)} className="text-ink-400 flex items-center"><X size={18} strokeWidth={2} /></button>
            </div>

            {/* Foto */}
            <div className="flex flex-col items-center mb-5">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-ink-900 flex items-center justify-center text-white text-2xl font-medium overflow-hidden">
                  {editFotoUrl
                    ? <img src={editFotoUrl} alt="" className="w-full h-full object-cover" />
                    : editNome.charAt(0).toUpperCase() || nome.charAt(0).toUpperCase()
                  }
                </div>
                {uploadingFoto && (
                  <div className="absolute inset-0 bg-black bg-opacity-40 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">…</span>
                  </div>
                )}
              </div>
              <input ref={fotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleFotoChange} />
              <button onClick={() => fotoInputRef.current.click()}
                className="mt-3 text-xs text-ink-500 border border-line rounded-lg px-4 py-2 hover:bg-paper transition-colors">
                Alterar foto
              </button>
            </div>

            <div className="flex flex-col gap-3">
              <div>
                <label className="text-xs text-ink-400 mb-1 block">Nome</label>
                <input type="text" value={editNome} onChange={e => setEditNome(e.target.value)}
                  className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
              </div>

              <div>
                <label className="text-xs text-ink-400 mb-1 flex items-center justify-between">
                  <span>@ usuário</span>
                  {editUsername && (() => {
                    const hint = usernameHintTexto(editUsernameStatus);
                    return hint ? <span className={`text-xs ${hint[0]}`}>{hint[1]}</span> : null;
                  })()}
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm text-ink-400 pointer-events-none">@</span>
                  <input type="text" value={editUsername}
                    onChange={e => setEditUsername(e.target.value.toLowerCase().replace(/[^a-z0-9.]/g, ''))}
                    placeholder="seunome"
                    className="w-full border border-line-soft rounded-xl pl-8 pr-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
                </div>
              </div>

              <div>
                <label className="text-xs text-ink-400 mb-1 flex items-center justify-between">
                  <span>Minibio</span>
                  <span className="text-ink-300">{editBio.length}/120</span>
                </label>
                <textarea value={editBio} onChange={e => setEditBio(e.target.value.slice(0, 120))}
                  placeholder="Uma linha sobre você..."
                  rows={2}
                  className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line resize-none"
                  style={{fontFamily:'inherit'}} />
              </div>

              <div>
                <label className="text-xs text-ink-400 mb-1 block">WhatsApp <span className="text-ink-300">(compartilhado em empréstimos)</span></label>
                <input type="tel" value={editWhatsapp}
                  onChange={e => setEditWhatsapp(e.target.value.replace(/[^0-9]/g, '').slice(0, 11))}
                  placeholder="11999999999"
                  className="w-full border border-line-soft rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-line" />
              </div>

              <div className="flex gap-2 mt-2">
                <button onClick={handleSalvarPerfil} disabled={salvandoPerfil}
                  className="flex-1 py-3 text-sm bg-brand text-white rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50">
                  {salvandoPerfil ? 'Salvando…' : 'Salvar'}
                </button>
                <button onClick={() => setModalEditar(false)}
                  className="flex-1 py-3 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EXCLUIR — aviso */}
      {etapaExcluir === 'aviso' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <p className="text-base font-medium text-ink-900 mb-2">Excluir conta?</p>
            {avisosAtivos > 0 ? (
              <p className="text-sm text-red-500 bg-red-50 rounded-xl p-3 mb-4">
                <span className="inline-flex items-center gap-1.5"><AlertTriangle size={14} color="#EF4444" strokeWidth={2} /> Você tem {avisosAtivos} empréstimo{avisosAtivos > 1 ? 's' : ''} ativo{avisosAtivos > 1 ? 's' : ''}. Resolva antes de excluir.</span>
              </p>
            ) : (
              <p className="text-sm text-ink-500 mb-4">
                Todos os seus dados serão removidos permanentemente: livros, histórico, avaliações e perfil. Essa ação é irreversível.
              </p>
            )}
            <div className="flex gap-2">
              {avisosAtivos === 0 && (
                <button onClick={() => setEtapaExcluir('confirmar')}
                  className="flex-1 py-2.5 text-sm text-red-500 border border-red-200 rounded-xl hover:bg-red-50 transition-colors">
                  Continuar
                </button>
              )}
              <button onClick={() => setEtapaExcluir(null)}
                className="flex-1 py-2.5 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EXCLUIR — confirmação */}
      {etapaExcluir === 'confirmar' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{background:'rgba(0,0,0,0.4)'}}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <p className="text-base font-medium text-ink-900 mb-1">Confirmação final</p>
            <p className="text-sm text-ink-500 mb-4">Digite <strong>EXCLUIR</strong> para confirmar.</p>
            <input type="text" value={confirmTexto} onChange={e => setConfirmTexto(e.target.value)}
              placeholder="EXCLUIR"
              className="w-full border border-line rounded-xl px-4 py-3 text-sm text-ink-900 bg-paper outline-none focus:border-red-300 mb-4" />
            <div className="flex gap-2">
              <button onClick={handleExcluirConta}
                disabled={confirmTexto !== 'EXCLUIR' || excluindo}
                className="flex-1 py-2.5 text-sm bg-red-500 text-white rounded-xl hover:bg-red-600 transition-colors disabled:opacity-40">
                {excluindo ? 'Excluindo…' : 'Excluir tudo'}
              </button>
              <button onClick={() => { setEtapaExcluir(null); setConfirmTexto(''); }}
                className="flex-1 py-2.5 text-sm border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default PerfilCompleto;
